import {
  Button,
  Dropdown,
  Field,
  Option,
  OptionGroup,
  Spinner,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import { HubApiError } from '@/api/client';
import type { ElevationScopeKind, PermissionPreset, ScopeCandidate } from '@/api/types';
import { SectionCard } from '@/design-system';
import { useHubToast } from '@/design-system/toast';
import { useEnvStore } from '@/env/envStore';
import { HUB_ENVS, HUB_ENV_FULL_LABEL, type HubEnv } from '@/env/types';
import { spacing } from '@/theme/tokens';

import {
  useElevationPresets,
  useElevationScopeCandidates,
  useSubmitElevation,
} from '../api';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.l,
  },
  full: {
    gridColumn: '1 / -1',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
  scopeRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 220px) 1fr',
    gap: spacing.m,
    alignItems: 'end',
  },
  // Renders the resolved ARM id underneath the scope row. NOT passed as
  // the Field's `hint` prop because ARM ids contain no whitespace or
  // hyphen breakpoints, so Fluent UI's default hint (no `overflow-wrap`)
  // overflows its Field cell to the left into the neighboring Scope-kind
  // column. Anchored as a row-spanning element with `overflow-wrap:
  // anywhere` so long ids wrap inside the row rather than spilling out.
  scopeId: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'anywhere',
    wordBreak: 'break-all',
    marginTop: spacing.xs,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing.s,
    marginTop: spacing.m,
  },
});

const DURATION_OPTIONS = [
  { hours: 1, label: '1 hour' },
  { hours: 4, label: '4 hours (default)' },
  { hours: 8, label: '8 hours' },
];

const SCOPE_KIND_LABEL: Record<ElevationScopeKind, string> = {
  Subscription: 'Subscription (sub-wide)',
  ResourceGroup: 'Resource group',
  Resource: 'Specific resource',
};

// Order shown in the kind dropdown — narrowest first so the picker
// nudges callers toward the smallest blast radius.
const SCOPE_KIND_ORDER: readonly ElevationScopeKind[] = [
  'Resource',
  'ResourceGroup',
  'Subscription',
];

// `HubApiError.message` is the generic `${status} ${statusText}` shape
// (e.g. `400 Bad Request`); the server-supplied diagnostic — like
// `Unknown preset 'foo'.` from /api/elevation/scope-candidates — lives
// in `.body`. Prefer the body string when it's present so the operator
// sees the actual problem, falling back to `.message` for plain
// `Error` instances (e.g. network failures inside hubFetch).
function formatHubApiError(err: Error): string {
  if (err instanceof HubApiError && typeof err.body === 'string' && err.body.trim().length > 0) {
    return err.body;
  }
  return err.message;
}

export function SubmitElevationForm() {
  const classes = useStyles();
  const toast = useHubToast();
  const presets = useElevationPresets();
  const submit = useSubmitElevation();
  const { env, setEnv } = useEnvStore();

  const [presetKey, setPresetKey] = useState<string>('');
  // `null` = "use the narrowest available kind" (the default the form
  // nudges toward). A non-null value means the user explicitly chose a
  // kind. Reset to null on preset change so a stale selection from a
  // previous preset never leaks through.
  const [userScopeKind, setUserScopeKind] = useState<ElevationScopeKind | null>(null);
  // Similarly, null id = "auto-pick if only one candidate exists". The
  // user's explicit choice replaces it.
  const [userScopeId, setUserScopeId] = useState<string | null>(null);
  const [justification, setJustification] = useState('');
  const [hours, setHours] = useState(4);

  const selectedPreset: PermissionPreset | undefined = useMemo(
    () => presets.data?.find((p) => p.key === presetKey),
    [presets.data, presetKey],
  );

  // Candidates depend on (env, presetKey). Both are part of the hook's
  // queryKey (see `useElevationScopeCandidates` in `../api.ts`), so any
  // env-switcher flip produces a new cache slot and a fresh fetch —
  // env-A's candidates can never leak through as env-B's. TanStack
  // doesn't observe request headers, only the queryKey; the env value
  // has to be in the key explicitly for this to work.
  const candidatesQuery = useElevationScopeCandidates(presetKey || undefined);

  // Group candidates by kind for OptionGroup rendering. Order: per SCOPE_KIND_ORDER.
  const candidatesByKind = useMemo(() => {
    const map: Record<ElevationScopeKind, ScopeCandidate[]> = {
      Resource: [],
      ResourceGroup: [],
      Subscription: [],
    };
    for (const c of candidatesQuery.data ?? []) map[c.kind].push(c);
    return map;
  }, [candidatesQuery.data]);

  // Which kinds are actually available for the selected preset? Sub-scoped
  // presets (MonitoringReader, SubscriptionContributor) return only one kind
  // from the backend; broader-scoped presets return all three.
  const availableKinds = useMemo(
    () => SCOPE_KIND_ORDER.filter((k) => candidatesByKind[k].length > 0),
    [candidatesByKind],
  );

  // Derive the active scope kind from (user choice, available kinds). The
  // explicit user choice wins when it's still valid for the current preset;
  // otherwise fall back to the narrowest available kind (first entry in
  // availableKinds, which is sorted Resource → ResourceGroup → Subscription
  // so the picker defaults to the smallest blast radius). Derived, not
  // useEffect-driven — eslint-plugin-react-hooks v7 rejects setState calls
  // in effects, and "reset stale state when input changes" is exactly the
  // pattern React's docs say to express as derived state instead. `null`
  // means "no kind available yet" (waiting on the preset / on the
  // candidates query).
  // `availableKinds[0]` is typed as `ElevationScopeKind` under our tsconfig
  // (`noUncheckedIndexedAccess` off), so an explicit `length === 0` branch
  // is needed to keep the union honest — otherwise `scopeKind` collapses to
  // never-null at the type level and lint flags the downstream null checks
  // as unreachable.
  const scopeKind: ElevationScopeKind | null =
    availableKinds.length === 0
      ? null
      : userScopeKind && availableKinds.includes(userScopeKind)
        ? userScopeKind
        : availableKinds[0];

  const visibleCandidates: readonly ScopeCandidate[] =
    scopeKind === null ? [] : candidatesByKind[scopeKind];

  // Similarly derived: explicit user pick if still in the visible list,
  // else auto-select when there's exactly one candidate (which is the
  // common case for Subscription-scoped presets), else empty.
  const scopeId =
    userScopeId && visibleCandidates.some((c) => c.id === userScopeId)
      ? userScopeId
      : visibleCandidates.length === 1
        ? visibleCandidates[0].id
        : '';

  const justMin = env === 'prod' && presetKey === 'subscription-contributor' ? 200 : 20;
  const justOk = justification.trim().length >= justMin;
  const submittable =
    !!selectedPreset && scopeKind !== null && scopeId.length > 0 && justOk;

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPreset || !submittable) return;
    submit.mutate(
      {
        env: env === 'dev' ? 'Dev' : env === 'staging' ? 'Staging' : 'Prod',
        presetKey: selectedPreset.key,
        scope: scopeId,
        justification: justification.trim(),
        durationMs: Math.min(hours, selectedPreset.maxDurationHours) * 60 * 60 * 1000,
      },
      {
        onSuccess: (created) => {
          toast({
            intent: created.status === 'AutoApproved' ? 'success' : 'info',
            title:
              created.status === 'AutoApproved'
                ? 'Auto-approved'
                : 'Submitted — awaiting approver',
            body: `Request ${created.id.slice(0, 8)} for ${selectedPreset.displayName}.`,
          });
          // Keep preset + scope selection so the operator can submit a
          // sibling request quickly; reset the per-incident fields.
          setJustification('');
        },
        onError: (err) => {
          toast({ intent: 'error', title: 'Submit failed', body: err.message });
        },
      },
    );
  };

  return (
    <SectionCard
      title="Request elevated access"
      description="Choose a preset and a scope. Dev auto-approves; staging requires a member of hub-elevation-approvers-staging; prod requires hub-elevation-approvers-prod."
    >
      <form className={classes.grid} onSubmit={handleSubmit}>
        <Field label="Environment">
          <Dropdown
            value={HUB_ENV_FULL_LABEL[env]}
            selectedOptions={[env]}
            onOptionSelect={(_, data) => {
              const next = data.optionValue;
              if (typeof next === 'string' && (HUB_ENVS as readonly string[]).includes(next)) {
                setEnv(next as HubEnv);
              }
            }}
          >
            {HUB_ENVS.map((e) => (
              <Option key={e} value={e} text={HUB_ENV_FULL_LABEL[e]}>
                {HUB_ENV_FULL_LABEL[e]}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Duration">
          <Dropdown
            value={DURATION_OPTIONS.find((d) => d.hours === hours)?.label ?? `${String(hours)}h`}
            selectedOptions={[String(hours)]}
            onOptionSelect={(_, data) => {
              const parsed = Number(data.optionValue);
              if (Number.isFinite(parsed) && parsed > 0) {
                setHours(parsed);
              }
            }}
          >
            {DURATION_OPTIONS.map((d) => (
              <Option key={d.hours} value={String(d.hours)} text={d.label}>
                {d.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Preset" className={classes.full} required>
          {presets.isLoading ? (
            <Spinner size="tiny" />
          ) : (
            <Dropdown
              placeholder="Select a permission preset…"
              value={selectedPreset?.displayName ?? ''}
              selectedOptions={presetKey ? [presetKey] : []}
              onOptionSelect={(_, data) => {
                setPresetKey(data.optionValue ?? '');
                // Clear explicit scope choices so the new preset's
                // narrowest-available kind takes effect.
                setUserScopeKind(null);
                setUserScopeId(null);
              }}
            >
              {(presets.data ?? []).map((p) => (
                <Option key={p.key} value={p.key} text={p.displayName}>
                  <div>
                    <Text weight="semibold">{p.displayName}</Text>
                    <div className={classes.hint}>
                      <Text size={200}>
                        {p.description} · max {p.maxDurationHours}h
                      </Text>
                    </div>
                  </div>
                </Option>
              ))}
            </Dropdown>
          )}
        </Field>

        <div className={`${classes.scopeRow} ${classes.full}`}>
          <Field label="Scope kind" required>
            <Dropdown
              placeholder={presetKey ? 'Choose a scope kind…' : 'Pick a preset first'}
              value={scopeKind ? SCOPE_KIND_LABEL[scopeKind] : ''}
              selectedOptions={scopeKind ? [scopeKind] : []}
              disabled={!presetKey || availableKinds.length === 0}
              onOptionSelect={(_, data) => {
                setUserScopeKind((data.optionValue as ElevationScopeKind | undefined) ?? null);
                // Clear the per-kind id so the auto-select for single-
                // candidate lists kicks in for the newly-chosen kind.
                setUserScopeId(null);
              }}
            >
              {availableKinds.map((k) => (
                <Option key={k} value={k} text={SCOPE_KIND_LABEL[k]}>
                  {SCOPE_KIND_LABEL[k]}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field
            label={
              scopeKind === 'Resource'
                ? `Resource${selectedPreset?.resourceKindFilter ? ` (${selectedPreset.resourceKindFilter})` : ''}`
                : scopeKind === 'ResourceGroup'
                  ? 'Resource group'
                  : scopeKind === 'Subscription'
                    ? 'Subscription'
                    // scopeKind === null: candidates haven't loaded yet OR
                    // no preset chosen yet. Use a kind-agnostic label so
                    // the row doesn't briefly say "Subscription" while
                    // the form is actually blank.
                    : 'Target'
            }
            required
            // Don't pass the ARM id as `hint=` — Fluent UI Field's hint
            // text has no `overflow-wrap`, and ARM ids contain no
            // whitespace / hyphen breakpoints, so a long id overflows
            // its Field cell to the LEFT into the Scope-kind column.
            // It's rendered separately below the scope row instead.
            //
            // Surface scope-candidates fetch failures inline so the form
            // doesn't just present an empty disabled dropdown — the
            // validationMessage explains what's wrong, and the Retry
            // button replaces the dropdown until the fetch succeeds.
            validationState={candidatesQuery.error ? 'error' : undefined}
            validationMessage={
              candidatesQuery.error
                ? `Couldn't load scope candidates: ${formatHubApiError(candidatesQuery.error)}`
                : undefined
            }
          >
            {candidatesQuery.isLoading ? (
              <Spinner size="tiny" />
            ) : candidatesQuery.error ? (
              <Button size="small" onClick={() => void candidatesQuery.refetch()}>
                Retry
              </Button>
            ) : (
              <Dropdown
                placeholder={
                  presetKey
                    ? scopeKind
                      ? 'Choose a target…'
                      : 'Pick a scope kind first'
                    : 'Pick a preset first'
                }
                value={
                  visibleCandidates.find((c) => c.id === scopeId)?.label ?? ''
                }
                selectedOptions={scopeId ? [scopeId] : []}
                disabled={!scopeKind || visibleCandidates.length === 0}
                onOptionSelect={(_, data) => { setUserScopeId(data.optionValue ?? null); }}
              >
                {scopeKind && visibleCandidates.length > 0 ? (
                  <OptionGroup label={SCOPE_KIND_LABEL[scopeKind]}>
                    {visibleCandidates.map((c) => (
                      <Option key={c.id} value={c.id} text={c.label}>
                        <div>
                          <Text>{c.label}</Text>
                          {c.resourceKind ? (
                            <div className={classes.hint}>
                              <Text size={200}>{c.resourceKind}</Text>
                            </div>
                          ) : null}
                        </div>
                      </Option>
                    ))}
                  </OptionGroup>
                ) : null}
              </Dropdown>
            )}
          </Field>
        </div>

        {/* Resolved ARM id, full-width below the scope row. See the
           `scopeId` style for why this isn't the Field's hint slot. */}
        {scopeId && !candidatesQuery.error ? (
          <div className={`${classes.scopeId} ${classes.full}`} aria-live="polite">
            {scopeId}
          </div>
        ) : null}

        <Field
          label={`Justification (≥ ${String(justMin)} chars)`}
          className={classes.full}
          required
          validationState={justification.length > 0 && !justOk ? 'warning' : undefined}
          validationMessage={
            justification.length > 0 && !justOk
              ? `Need ${String(justMin - justification.trim().length)} more chars.`
              : undefined
          }
        >
          <Textarea
            rows={3}
            value={justification}
            onChange={(_, data) => { setJustification(data.value); }}
            placeholder="What you're investigating, why this scope, and which incident or request this relates to."
          />
        </Field>

        <div className={classes.actions} style={{ gridColumn: '1 / -1' }}>
          <Button appearance="primary" type="submit" disabled={!submittable || submit.isPending}>
            {submit.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}
